import React, { useState, useEffect } from 'react';
import { Bell, X, CheckCircle } from 'lucide-react';

/**
 * PUSH NOTIFICATION OPT-IN BANNER
 * Prompts users to enable push notifications
 * Shows on dashboard after login if not subscribed
 */

const PushNotificationBanner = ({ onSubscribe, onDismiss }) => {
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    // Check if push notifications are supported
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setIsSupported(false);
      return;
    }
    
    // Check if already dismissed
    const dismissed = localStorage.getItem('push_notification_dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setIsDismissed(true);
        return;
      }
    }
    
    // Check if already subscribed
    try {
      const permission = Notification.permission;
      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          setIsSubscribed(true);
        }
      }
    } catch (e) {
      console.log('Error checking notification status:', e);
    }
  };

  const handleSubscribe = async () => {
    setIsSubscribing(true);
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        alert('Please allow notifications in your browser settings to receive updates.');
        setIsSubscribing(false);
        return;
      }
      
      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID public key from server
      const vapidResponse = await fetch(`${API_URL}/api/push/vapid-public-key`);
      const vapidData = await vapidResponse.json();
      
      if (!vapidData.public_key) {
        console.error('No VAPID public key available');
        setIsSubscribing(false);
        return;
      }
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidData.public_key)
      });
      
      // Send subscription to server
      const token = localStorage.getItem('access_token');
      await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      });
      
      setIsSubscribed(true);
      if (onSubscribe) onSubscribe();
      
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      alert('Failed to enable notifications. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('push_notification_dismissed', Date.now().toString());
    setIsDismissed(true);
    if (onDismiss) onDismiss();
  };

  // Convert VAPID key
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Don't show if not supported, already subscribed, or dismissed
  if (!isSupported || isSubscribed || isDismissed) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-xl p-4 mb-6 shadow-lg">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
          <Bell className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-lg mb-1">Stay Updated!</h3>
          <p className="text-white/90 text-sm mb-3">
            Enable notifications to receive alerts about new sessions, appointment reminders, 
            mentor feedback, and community messages.
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleSubscribe}
              disabled={isSubscribing}
              className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="enable-notifications-btn"
            >
              {isSubscribing ? (
                <>
                  <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                  Enabling...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  Enable Notifications
                </>
              )}
            </button>
            <button
              onClick={handleDismiss}
              className="text-white/80 hover:text-white px-3 py-2 text-sm"
            >
              Maybe Later
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-white/20 rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

/**
 * SUCCESS TOAST after enabling notifications
 */
export const NotificationEnabledToast = () => (
  <div className="flex items-center gap-3 bg-green-50 text-green-800 p-4 rounded-lg border border-green-200">
    <CheckCircle className="w-6 h-6 text-green-600" />
    <div>
      <p className="font-medium">Notifications Enabled!</p>
      <p className="text-sm text-green-600">You'll now receive updates about sessions, feedback, and more.</p>
    </div>
  </div>
);

export default PushNotificationBanner;
